import React from 'react';
import Button from 'react-bootstrap/Button';
import { VscSend } from 'react-icons/vsc';

import Tooltip from '../../../../../controls/common/tooltip';

import { ANSWER_ROWS } from '../../components/autoGrow';
import GrowTextarea from '../../components/growTextarea';
import { MarkdownBody } from '../../../common/markdown';
import { choiceForAnswer } from '../../domain/cellObjects';

/**
 * QuestionInput — ONE question, in the one appearance a question has.
 *
 * Every way a cell can be asked something goes through here: an approval's two labels, a
 * single-select, a multi-select, free text on its own, and free text offered beside options. So does
 * the read-only rendering of each — a question that has been answered, or that nobody is waiting on
 * any more, is the same rows with the answer marked rather than a different component's idea of what
 * a question looks like.
 *
 * That was the point of extracting it. The live and historical renderings were two near-identical
 * copies of the same markup, and a free-text question had a THIRD spelling — a composer pill instead
 * of a row — so what the field looked like depended on whether the question happened to offer
 * options. Anything added here from now on (an attachment, an image) lands in every question of
 * every kind at once, which it could not before.
 *
 * The shape is a numbered list: one row per choice, and — when the question takes free text — a last
 * row that IS the field, numbered among them, because "something else" is one of the answers rather
 * than a separate act. A question with no choices at all is that row alone, unnumbered: there is
 * nothing for it to be the nth of.
 *
 * Read-only is the same list with nothing to press: rows become plain, the chosen ones keep their
 * mark and the rest dim. An answer matching no option is somebody's own words, and appears as the
 * row it effectively was. A free-text question NOBODY answered says so — it used to render as
 * nothing at all, so a question asked and left unanswered vanished from its own record.
 *
 * Props:
 *   question    — one entry from `inFlightQuestions`: { prompt, mode, choices, freeText }
 *   headed      — render the question's own prompt above it (false when the record's prose is it)
 *   readOnly    — history: no controls, the answer marked
 *   picked      — string[]  the labels chosen so far (live)
 *   typed       — string    the free-text box's contents (live)
 *   answer      — string[]  what it was answered with (read-only)
 *   busy        — a send is in flight
 *   onPick      — (choice) => void
 *   onType      — (text) => void
 *   onSend      — when given, this question's own row carries the commit (a send icon), and Enter
 *                 in the field is that same commit. The two are ONE decision on purpose: a field
 *                 with a send button and no Enter is the composer's contract half-kept, and that
 *                 is exactly how a gate's field ended up taking a newline where the thread's takes
 *                 a send. No caller can get them out of step because no caller sets them apart.
 *   sendDisabled
 *   placeholder — overrides the field's own wording. A GATE's field is not an alternative answer,
 *                 it is comments that become the run's content, and it says so.
 */
export default function QuestionInput({
  question, headed, readOnly, picked, typed, answer, busy,
  onPick, onType, onSend, sendDisabled, placeholder
}) {
  // Enter sends, Shift+Enter is a newline — the composer's contract, wherever a field commits.
  const handleKeyDown = onSend
    ? (event) => {
      if (event.key !== 'Enter' || event.shiftKey) return;
      event.preventDefault();
      if (!sendDisabled) onSend();
    }
    : undefined;
  // A recorded answer value is whichever of a choice's two strings the answering surface wrote —
  // this app posts `text`, a person typing into the raw thread writes anything — so read-only
  // rendering resolves each value through the same lookup the posting side and the worker's bridge
  // agree on, rather than comparing it to `label` alone.
  const resolved = readOnly ? (answer || []).map(value => choiceForAnswer(question.choices, value)) : [];
  const chosen = new Set(readOnly
    ? resolved.filter(Boolean).map(choice => choice.label)
    : (picked || []));
  // An answer matching no choice at all is somebody's own words — the "Other" path — and is shown
  // as the row it effectively was rather than dropped for not being an option.
  const own = readOnly
    ? (answer || []).filter((value, index) => value && !resolved[index])
    : [(typed || '')].filter(Boolean);
  const numbered = question.choices.length > 0;

  const row = (key, position, body, extra) => {
    const on = extra && extra.on;
    const className = `bh-choice${on ? ' on' : (readOnly ? ' bh-choice-off' : '')}`;
    const inner = (
      <>
        {/* Decoration for the eye, not part of the control's name: a reader announcing "1 Approve"
            buries the answer under its position. */}
        {numbered && (
          <span className="bh-choice-number" aria-hidden="true">{position}</span>
        )}
        {body}
      </>
    );
    if (!extra || !extra.onClick) return <div key={key} className={className}>{inner}</div>;
    return (
      <button key={key} type="button" className={className} disabled={busy}
        aria-pressed={!!on} onClick={extra.onClick}>{inner}</button>
    );
  };

  return (
    <>
      {/* The question's own text. Not skipped for a lone question: the record's body is not that
          text, it is the whole rendered prompt with the options inside it, and leaning on it is
          what showed every option twice. */}
      {headed && question.prompt && (
        <MarkdownBody text={question.prompt} className="mc-question" />
      )}

      <div className="bh-choice-list">
        {question.choices.map((choice, position) => row(
          choice.label, position + 1,
          <span className="bh-choice-body">
            <span className="bh-choice-label">{choice.label}</span>
            {choice.description && (
              <span className="bh-choice-description d-block">{choice.description}</span>
            )}
          </span>,
          {
            on: chosen.has(choice.label),
            onClick: readOnly ? null : () => onPick(choice),
          }
        ))}

        {/* The last row: an answer of your own, numbered with the options because it is one of them
            — but never on an APPROVAL, whose two labels are the whole of what the worker's prompt
            bridge can read back. Free text there is a Deny wearing a "yes". */}
        {/* `on` while it holds the answer — it is one of the options, and the one being chosen.
            Without it, typing left every row unlit and nothing said where the answer was. */}
        {question.freeText && !readOnly && (
          <div className={`bh-choice bh-choice-entry${(typed || '').trim() ? ' on' : ''}`}>
            {numbered && (
              <span className="bh-choice-number" aria-hidden="true">
                {question.choices.length + 1}
              </span>
            )}
            {/* Grows with what is typed into it. At a fixed row it scrolled, so a second line
                pushed the first out of sight — in a control whose whole job is to let somebody say
                the thing the options did not cover, which is exactly the answer likely to run past
                one line. ANSWER_ROWS is where it stops and scrolls, so a long answer cannot push
                the questions under it off screen. */}
            <GrowTextarea
              rows={1}
              maxRows={ANSWER_ROWS}
              className="bh-choice-input bh-choice-body"
              placeholder={placeholder
                || (numbered ? 'Something else — type it here' : 'Your answer…')}
              value={typed || ''}
              disabled={busy}
              onChange={onType}
              onKeyDown={handleKeyDown}
            />
            {onSend && (
              // The app's tooltip, not the browser's `title` — same control, same hint, same way
              // of showing it as the thread composer's send.
              <Tooltip text="Send (Shift+Enter for newline)">
                <Button className="bh-composer-send" aria-label="Send"
                  disabled={sendDisabled} onClick={onSend}>
                  <VscSend />
                </Button>
              </Tooltip>
            )}
          </div>
        )}

        {/* Read-only, what was typed — or the fact that nothing was. A question asked and never
            answered used to render as an empty box: the field went with the waiting, and nothing
            took its place, so the record lost the only trace that anything had been asked of it. */}
        {/* Their own words, rendered the way they wrote them: markdown, with a newline meaning a
            newline (`breaks`), because it was typed in a field where Shift+Enter is one. Flat text
            printed their fences and ran their lines together.

            And not bold. `bh-choice-label` is the weight that marks WHICH option was taken, which is
            a mark that only means something among options; on a free-text answer there is nothing to
            distinguish it from, and the row is prose — often several lines of it. The question above
            it makes the same distinction, bolding only its lead line (`.mc-question`). */}
        {readOnly && own.length > 0 && row('own', question.choices.length + 1,
          <MarkdownBody text={own.join(', ')} breaks className="bh-choice-body mc-answer" />,
          { on: true })}
        {readOnly && own.length === 0 && chosen.size === 0 && row('none', question.choices.length + 1,
          <span className="bh-choice-body text-muted">Not answered</span>, null)}
      </div>
    </>
  );
}
